import { css, cx } from "@emotion/css";
import { Button, CustomMenu, Divider, InlineMessage, MenuItemButton, Switch, useMenuState } from "@octopusdeploy/design-system-components";
import { CheckCircleIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDApplicationResource, ArgoCDApplicationSourceResource, EnvironmentV2Resource, ProjectResource, TenantResource } from "@octopusdeploy/octopus-server-client";
import { ArgoCDAnnotationKeys, isHelmApplicationSource, Permission } from "@octopusdeploy/octopus-server-client";
import { sortBy } from "lodash";
import React, { useMemo, useState } from "react";
import { EnvironmentsWithMissingChipList } from "~/areas/infrastructure/components/ArgoCDInstances/components/EnvironmentsChipList";
import { ExpandingChipList } from "~/areas/infrastructure/components/ArgoCDInstances/components/ExpandingChipList";
import { SourceTypeChip } from "~/areas/infrastructure/components/ArgoCDInstances/components/SourceTypeChip";
import { ConnectExistingGitCredentialDrawer } from "~/areas/library/components/GitCredentials/ConnectExistingGitCredentialDrawer";
import { GitCredentialDrawer } from "~/areas/library/components/GitCredentials/GitCredentialDrawer";
import { projectChipWithMissingList, tenantChipWithMissingList } from "~/components/Chips/index";
import { Code } from "~/components/Code/Code";
import { PermissionCheck } from "~/components/PermissionCheck/index";
const styles = {
    container: css({
        backgroundColor: themeTokens.color.background.secondary.default,
        display: "flex",
        flexDirection: "column",
        gap: space[32],
        padding: space[16],
    }),
    sectionContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    heading: css({
        font: text.heading.small,
        display: "flex",
        flexDirection: "row",
        gap: space[8],
        alignItems: "center",
    }),
    itemContainer: css({
        padding: space[16],
        borderRadius: borderRadius["large"],
        backgroundColor: themeTokens.color.background.primary.default,
        boxShadow: themeTokens.shadow.extraSmall,
        display: "flex",
        flexDirection: "column",
        gap: space[16],
    }),
    repositoryUrlWithIcon: css({
        font: text.body.bold.medium,
        color: themeTokens.color.text.primary,
        display: "flex",
        flexDirection: "row",
        gap: space[4],
        alignItems: "center",
        margin: 0,
    }),
    codeContainer: css({
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        borderRadius: borderRadius["small"],
        //this is required for the outer border radius to properly clip content
        overflow: "auto",
    }),
    applicationSourceHeaderRow: css({ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", width: "100%" }),
    sourcePropertiesRow: css({ display: "flex", flexDirection: "row", justifyContent: "flex-start", alignItems: "center", width: "100%", gap: space[12], height: space[24] }),
    sourcePropertiesLabelColumn: css({ width: "120px", color: themeTokens.color.text.secondary }),
    applicationSourcePropertiesValueColumn: css({
        display: "flex",
        alignItems: "center",
    }),
    applicationSourcePropertiesValueColumnWithChip: css({
        display: "flex",
        alignItems: "center",
        marginLeft: "-0.25rem",
    }),
    //Chips have a 0.25rem margin
    chipDefaultMargin: css({
        marginLeft: space[4],
    }),
    connectGitCredentials: css({ display: "flex", flexDirection: "row-reverse", justifyContent: "flex-start", alignItems: "center" }),
    itemWarningSubtitle: css({
        font: text.body.regular.medium,
        color: themeTokens.color.text.secondary,
    }),
};
function getSourcesWithRequiringGitCredentials(application: ArgoCDApplicationResource) {
    return application.Sources.filter((src) => src.RequiresGitCredential);
}
export function hasSourcesWithoutMatchedGitCredentials(application: ArgoCDApplicationResource) {
    //we don't require git credentials
    return getSourcesWithRequiringGitCredentials(application).some((src) => !src.BestMatchedGitCredentialId);
}
export function isSourceMissingRequireAnnotations(source: ArgoCDApplicationSourceResource, application: ArgoCDApplicationResource): boolean {
    const containsMultipleSources = application.Sources.length > 1;
    const missingProject = getProjectAnnotation(containsMultipleSources, source, application.Annotations) === undefined;
    const missingEnvironment = getEnvironmentAnnotation(containsMultipleSources, source, application.Annotations) === undefined;
    //If the source isn't scoped at all (not relevant)
    if (missingProject && missingEnvironment) {
        return false;
    }
    return missingProject || missingEnvironment;
}
export function isMissingRequiredAnnotations(application: ArgoCDApplicationResource) {
    return application.Sources.some((source) => isSourceMissingRequireAnnotations(source, application));
}
function getHelmImagePathAnnotation(containsMultipleSources: boolean, source: ArgoCDApplicationSourceResource, annotations: Partial<Record<string, string>>): string | undefined {
    if (!isHelmApplicationSource(source)) {
        return undefined;
    }
    if (containsMultipleSources && !source.Name) {
        return undefined;
    }
    return annotations[ArgoCDAnnotationKeys.OctopusImageReplacementPathsKey(source.Name)];
}
function getAllHelmImagePathAnnotations(application: ArgoCDApplicationResource) {
    const containsMultipleSources = application.Sources.length > 1;
    return application.Sources.reduce((dict, source) => {
        dict[source.Index] = getHelmImagePathAnnotation(containsMultipleSources, source, application.Annotations);
        return dict;
    }, 
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as Record<number, string | undefined>);
}
function sortAndFilterAnnotations(annotations: Partial<Record<string, string>>, showAllAnnotations: boolean) {
    const sortedAnnotations = sortBy(Object.entries(annotations), ([key]) => {
        //the key might be a prefix (image replace paths), so we look through the sort orders and compare using prefixes
        const [, sortOrder] = Object.entries(annotationSortOrders).find(([k]) => key.startsWith(k)) || ["", unknownAnnotationSortOrder];
        return sortOrder;
    });
    return sortedAnnotations.filter(([key]) => !key.startsWith("kubectl.kubernetes.io/")).filter(([key]) => showAllAnnotations || key.startsWith("argo.octopus.com"));
}
type ArgoCDApplicationDetailsProps = {
    application: ArgoCDApplicationResource;
    onGitCredentialAdded: () => void;
    onGenerateAnnotationsClick: (applicationSourceIndex: number | undefined) => void;
    projects: ProjectResource[];
    environments: EnvironmentV2Resource[];
    tenants: TenantResource[];
};
const annotationSortOrders: Record<string, number> = {
    [ArgoCDAnnotationKeys.ProjectPrefix]: 1,
    [ArgoCDAnnotationKeys.EnvironmentPrefix]: 2,
    [ArgoCDAnnotationKeys.TenantPrefix]: 3,
    [ArgoCDAnnotationKeys.ImageReplacePathsPrefix]: 4,
    [ArgoCDAnnotationKeys.DefaultImageRegistry]: 5,
};
// We set any unknown annotations sort order to be much higher to force them at the end of the list
const unknownAnnotationSortOrder = 100;
type ConnectGitCredentialMode = "Add" | "Connect";
function getEnvironmentAnnotation(containsMultipleSources: boolean, source: ArgoCDApplicationSourceResource, annotations: Partial<Record<string, string>>) {
    if (containsMultipleSources && !source.Name) {
        return undefined;
    }
    return annotations[ArgoCDAnnotationKeys.OctopusEnvironmentAnnotationKey(source.Name)];
}
function getAllEnvironmentAnnotations(application: ArgoCDApplicationResource) {
    const containsMultipleSources = application.Sources.length > 1;
    return application.Sources.reduce((dict, source) => {
        dict[source.Index] = getEnvironmentAnnotation(containsMultipleSources, source, application.Annotations);
        return dict;
    }, 
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as Record<number, string | undefined>);
}
function getProjectAnnotation(containsMultipleSources: boolean, source: ArgoCDApplicationSourceResource, annotations: Partial<Record<string, string>>) {
    if (containsMultipleSources && !source.Name) {
        return undefined;
    }
    return annotations[ArgoCDAnnotationKeys.OctopusProjectAnnotationKey(source.Name)];
}
function getAllProjectAnnotations(application: ArgoCDApplicationResource) {
    const containsMultipleSources = application.Sources.length > 1;
    return application.Sources.reduce((dict, source) => {
        dict[source.Index] = getProjectAnnotation(containsMultipleSources, source, application.Annotations);
        return dict;
    }, 
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as Record<number, string | undefined>);
}
function getTenantAnnotation(containsMultipleSources: boolean, source: ArgoCDApplicationSourceResource, annotations: Partial<Record<string, string>>) {
    if (containsMultipleSources && !source.Name) {
        return undefined;
    }
    return annotations[ArgoCDAnnotationKeys.OctopusTenantAnnotationKey(source.Name)];
}
function getAllTenantAnnotations(application: ArgoCDApplicationResource) {
    const containsMultipleSources = application.Sources.length > 1;
    return application.Sources.reduce((dict, source) => {
        dict[source.Index] = getTenantAnnotation(containsMultipleSources, source, application.Annotations);
        return dict;
    }, 
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as Record<number, string | undefined>);
}
export const ArgoCDApplicationDetails = (props: ArgoCDApplicationDetailsProps) => {
    const { application, onGitCredentialAdded } = props;
    const [showAllAnnotations, setShowAllAnnotations] = useState<boolean>(false);
    const [openMenu, menuState, buttonAriaAttributes] = useMenuState();
    const [appSourceToAddCredentialFor, setAppSourceToAddCredentialFor] = useState<{
        source: ArgoCDApplicationSourceResource;
        mode: ConnectGitCredentialMode;
    } | undefined>(undefined);
    const projectAnnotationsForAllSources = useMemo(() => getAllProjectAnnotations(application), [application]);
    const environmentAnnotationsForAllSources = useMemo(() => getAllEnvironmentAnnotations(application), [application]);
    const tenantAnnotationsForAllSources = useMemo(() => getAllTenantAnnotations(application), [application]);
    const helmAnnotationsForAllSources = useMemo(() => getAllHelmImagePathAnnotations(application), [application]);
    const annotationsYaml = useMemo(() => {
        let annotationYaml = `annotations:`;
        const filteredAnnotations = sortAndFilterAnnotations(application.Annotations, showAllAnnotations);
        for (const [key, value] of filteredAnnotations) {
            if (value) {
                annotationYaml += `\n  ${key}: "${value}"`;
            }
        }
        return annotationYaml;
    }, [application.Annotations, showAllAnnotations]);
    const containsUnscopedAnnotationWhenMultipleSourcesExist = useMemo(() => {
        const containsMultipleSources = application.Sources.length > 1;
        if (!containsMultipleSources) {
            return false;
        }
        const project = application.Annotations[ArgoCDAnnotationKeys.OctopusProjectAnnotationKey(null)];
        const environment = application.Annotations[ArgoCDAnnotationKeys.OctopusEnvironmentAnnotationKey(null)];
        const tenant = application.Annotations[ArgoCDAnnotationKeys.OctopusTenantAnnotationKey(null)];
        const imagePath = application.Annotations[ArgoCDAnnotationKeys.OctopusImageReplacementPathsKey(null)];
        return project || environment || tenant || imagePath;
    }, [application.Annotations, application.Sources]);
    const sources = application.Sources;
    return (<>
            <div className={styles.container}>
                {sources.length > 0 && (<div className={styles.sectionContainer}>
                        <div className={styles.heading}>Sources</div>
                        {sources.map((source) => {
                const hasEnvironmentAnnotation = environmentAnnotationsForAllSources[source.Index] !== undefined;
                const hasProjectAnnotation = projectAnnotationsForAllSources[source.Index] !== undefined;
                const helmImageTemplateAnnotationValue = helmAnnotationsForAllSources[source.Index];
                const hasHelmImageTemplateAnnotation = helmImageTemplateAnnotationValue !== undefined;
                const hasTenantAnnotation = tenantAnnotationsForAllSources[source.Index] !== undefined;
                const hasScopingAnnotations = hasProjectAnnotation || hasEnvironmentAnnotation;
                const hasOctopusAnnotations = hasScopingAnnotations || hasHelmImageTemplateAnnotation;
                return (<div key={source.Index} className={cx(styles.itemContainer)}>
                                    <div className={styles.applicationSourceHeaderRow}>
                                        <div>
                                            <div className={styles.repositoryUrlWithIcon}>
                                                {source.BestMatchedGitCredentialId && <CheckCircleIcon size={20} color={themeTokens.color.icon.success}/>}
                                                {source.RepositoryUrl}
                                            </div>
                                            {!hasOctopusAnnotations && <div className={styles.itemWarningSubtitle}>No Octopus annotations defined</div>}
                                            {hasScopingAnnotations && !source.BestMatchedGitCredentialId && <InlineMessage type={"warning"} description={"Missing Git credentials"}></InlineMessage>}
                                        </div>
                                        <div className={styles.connectGitCredentials}>
                                            {hasScopingAnnotations && !source.BestMatchedGitCredentialId && (<PermissionCheck permission={Permission.GitCredentialEdit}>
                                                    <Button importance={"tertiary"} label={"Connect Git Credential"} size={"medium"} onClick={openMenu} {...buttonAriaAttributes}/>
                                                    <CustomMenu accessibleName={"Connect Git Credential"} menuState={menuState}>
                                                        <MenuItemButton title={"Create a new Git credential"} onClick={() => {
                            menuState.onClose();
                            setAppSourceToAddCredentialFor({ source: source, mode: "Add" });
                        }}>
                                                            Create a new Git credential
                                                        </MenuItemButton>
                                                        <MenuItemButton title={"Connect an existing Git credential"} onClick={() => {
                            menuState.onClose();
                            setAppSourceToAddCredentialFor({ source: source, mode: "Connect" });
                        }}>
                                                            Connect an existing Git credential
                                                        </MenuItemButton>
                                                    </CustomMenu>
                                                </PermissionCheck>)}
                                        </div>
                                    </div>
                                    <Divider></Divider>
                                    {source.Name && (<div className={styles.sourcePropertiesRow}>
                                            <div className={styles.sourcePropertiesLabelColumn}>Name</div>
                                            <div className={styles.applicationSourcePropertiesValueColumn}> {source.Name}</div>
                                        </div>)}
                                    {hasScopingAnnotations && (<>
                                            <div className={styles.sourcePropertiesRow}>
                                                <div className={styles.sourcePropertiesLabelColumn}>Project</div>
                                                <div className={styles.applicationSourcePropertiesValueColumnWithChip}>
                                                    <ExpandingChipList singularName={"project"} ids={[source.ProjectId].filter((id) => id !== null)} renderChipList={(ids) => projectChipWithMissingList(props.projects, ids)}/>
                                                    {hasProjectAnnotation && source.ProjectId === null && (<div className={styles.chipDefaultMargin}>
                                                            <InlineMessage type={"warning"} description={"Annotation value doesn't match any projects"} action={{ label: "Regenerate annotations", onClick: () => props.onGenerateAnnotationsClick(source.Index) }}></InlineMessage>
                                                        </div>)}
                                                    {!hasProjectAnnotation && (<div className={styles.chipDefaultMargin}>
                                                            <InlineMessage type={"warning"} description={"Missing project annotation"} action={{ label: "Regenerate annotations", onClick: () => props.onGenerateAnnotationsClick(source.Index) }}></InlineMessage>
                                                        </div>)}
                                                </div>
                                            </div>
                                            <div className={styles.sourcePropertiesRow}>
                                                <div className={styles.sourcePropertiesLabelColumn}>Environment</div>
                                                <div className={styles.applicationSourcePropertiesValueColumnWithChip}>
                                                    <EnvironmentsWithMissingChipList environmentIds={[source.EnvironmentId].filter((id) => id !== null)} environments={props.environments}/>
                                                    {hasEnvironmentAnnotation && source.EnvironmentId === null && (<div className={styles.chipDefaultMargin}>
                                                            <InlineMessage type={"warning"} description={"Annotation value doesn't match any environments"} action={{ label: "Regenerate annotations", onClick: () => props.onGenerateAnnotationsClick(source.Index) }}></InlineMessage>
                                                        </div>)}
                                                    {!hasEnvironmentAnnotation && (<div className={styles.chipDefaultMargin}>
                                                            <InlineMessage type={"warning"} description={"Missing environment annotation"} action={{ label: "Regenerate annotations", onClick: () => props.onGenerateAnnotationsClick(source.Index) }}></InlineMessage>
                                                        </div>)}
                                                </div>
                                            </div>

                                            {hasTenantAnnotation && (<div className={styles.sourcePropertiesRow}>
                                                    <div className={styles.sourcePropertiesLabelColumn}>Tenant</div>
                                                    <div className={styles.applicationSourcePropertiesValueColumnWithChip}>
                                                        <ExpandingChipList singularName={"tenant"} ids={[source.TenantId].filter((id) => id !== null)} renderChipList={(ids) => tenantChipWithMissingList(props.tenants, ids)}/>
                                                        {source.TenantId === null && (<div className={styles.chipDefaultMargin}>
                                                                <InlineMessage type={"warning"} description={"Annotation value doesn't match any tenant"} action={{ label: "Regenerate annotations", onClick: () => props.onGenerateAnnotationsClick(source.Index) }}></InlineMessage>
                                                            </div>)}
                                                    </div>
                                                </div>)}
                                        </>)}
                                    <div className={styles.sourcePropertiesRow}>
                                        <div className={styles.sourcePropertiesLabelColumn}>Type</div>
                                        <div className={styles.applicationSourcePropertiesValueColumn}>
                                            <SourceTypeChip source={source}></SourceTypeChip>
                                        </div>
                                    </div>
                                    {isHelmApplicationSource(source) && hasHelmImageTemplateAnnotation && (<div className={styles.sourcePropertiesRow}>
                                            <div className={styles.sourcePropertiesLabelColumn}>Image template</div>
                                            <div className={styles.applicationSourcePropertiesValueColumn}>{helmImageTemplateAnnotationValue}</div>
                                        </div>)}
                                </div>);
            })}
                    </div>)}
                <div className={styles.sectionContainer}>
                    <div className={styles.heading}>
                        <span> Application annotations</span>
                    </div>
                    <div className={styles.itemContainer}>
                        {containsUnscopedAnnotationWhenMultipleSourcesExist && (<InlineMessage type={"warning"} description={"Multi-source applications require source-scoped annotations"} action={{ label: "Learn more", onClick: () => props.onGenerateAnnotationsClick(undefined) }}></InlineMessage>)}
                        <div className={styles.codeContainer}>
                            <Code value={annotationsYaml} language={"YAML"} showLineNumbers={false} showCodeFold={false} lineWrapping={true}/>
                        </div>
                        <div>
                            <Switch value={showAllAnnotations} onChange={setShowAllAnnotations} label={"Show all annotations"} size={"small"}/>
                        </div>
                    </div>
                </div>
            </div>
            <GitCredentialDrawer isOpen={!!appSourceToAddCredentialFor && appSourceToAddCredentialFor.mode === "Add"} onClose={() => setAppSourceToAddCredentialFor(undefined)} onSave={() => {
            onGitCredentialAdded();
            setAppSourceToAddCredentialFor(undefined);
        }} initialAllowedRepositoryUrls={appSourceToAddCredentialFor ? [appSourceToAddCredentialFor.source.RepositoryUrl] : undefined}/>
            <ConnectExistingGitCredentialDrawer isOpen={!!appSourceToAddCredentialFor && appSourceToAddCredentialFor.mode === "Connect"} onClose={() => setAppSourceToAddCredentialFor(undefined)} additionalAllowedRepositoryUrls={appSourceToAddCredentialFor ? [appSourceToAddCredentialFor.source.RepositoryUrl] : undefined} onSave={() => {
            onGitCredentialAdded();
            setAppSourceToAddCredentialFor(undefined);
        }}/>
        </>);
};
